import { User, Building2, BadgeCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { PageCTA } from "../components/PageCTA";
import { Reveal, Overline } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { EXTRA } from "../content/extra";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/aac30769-f491-4e91-84b6-42abbea5b108/images/480ed47abb8837de4d45868c027f49c82d0159dab0458873a2209c646b6a7ef8.jpeg";

const Track = ({ icon: Icon, code, title, text, steps }) => (
  <div className="border border-white/10 p-8 md:p-10 h-full">
    <div className="flex items-center gap-4 mb-4">
      <span className="w-12 h-12 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
        <Icon size={22} strokeWidth={1.5} />
      </span>
      <span className="text-xs tracking-[0.2em] uppercase text-white/40">{code}</span>
    </div>
    <h3 className="font-display text-2xl md:text-3xl mb-3">{title}</h3>
    <p className="text-white/55 leading-relaxed mb-8">{text}</p>
    <div className="space-y-px bg-white/10">
      {steps.map((st, i) => (
        <div key={i} className="flex items-start gap-5 bg-[#0a0a0a] p-5">
          <span className="font-display text-2xl text-[#D4AF37]/70">{String(i + 1).padStart(2, "0")}</span>
          <div>
            <p className="font-medium">{st.title}</p>
            <p className="text-white/50 text-sm">{st.text}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function Certification() {
  const { t, lang } = useLang();
  const c = t.pages.certification;
  const cert = EXTRA[lang].certificate;

  return (
    <div data-testid="certification-page">
      <PageHero overline={c.overline} title={c.title} image={HERO_IMG} />

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Overline>{c.overline}</Overline>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-8 mt-8">
            <Reveal>
              <Track
                icon={User}
                code="Sa.Eko.Prs-2026"
                title={c.individualsTitle}
                text={c.individualsText}
                steps={c.individualSteps}
              />
            </Reveal>
            <Reveal delay={0.15}>
              <Track
                icon={Building2}
                code="Sa.Eko.Fa-2026"
                title={c.companiesTitle}
                text={c.companiesText}
                steps={c.companySteps}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Real certificate showcase */}
      <section
        data-testid="certificate-showcase"
        className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-y border-white/5 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <Overline>{cert.overline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">{cert.title}</h2>
            <p className="text-white/60 text-lg leading-relaxed mt-8">{cert.text}</p>
            <div className="inline-flex items-center gap-3 mt-8 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-5 py-3">
              <BadgeCheck size={20} className="text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-[#F2E3C6] tracking-wide text-sm uppercase">{cert.badge}</span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative flex justify-center perspective-[1200px]">
              <div className="absolute -inset-6 bg-[#D4AF37]/10 blur-3xl rounded-full" />
              <img
                src="/certifikat-saubereko.jpg"
                alt={cert.title}
                data-testid="certificate-image"
                className="relative w-full max-w-md rounded-sm shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-[#D4AF37]/30 -rotate-3 hover:rotate-0 transition-transform duration-700 ease-out"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <PageCTA label={t.pages.common.partnership} />
    </div>
  );
}
